import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import styles from "./multiSelectBox.module.scss";
import Select, { components } from "react-select";

const ALL_OPTION = { value: "__select_all__", label: "All" };

const AllValueContainer = ({ children, ...props }) => {
  if (props.selectProps.isAllSelected) {
    return (
      <components.ValueContainer {...props}>
        <div style={{ color: "inherit", marginLeft: 2 }}>All</div>
        {children[1]}
      </components.ValueContainer>
    );
  }
  return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
};

const CheckboxOption = (props) => {
  const { label, showCheckbox, selectProps } = props;
  const isChecked = selectProps.isAllSelected || props.isSelected;
  return (
    <components.Option {...props}>
      {showCheckbox && <input type="checkbox" checked={isChecked} readOnly style={{ marginRight: 8 }} />}
      {label}
    </components.Option>
  );
};

export const MultiSelect = forwardRef(
  (
    {
      classes,
      id = "",
      label,
      name = "",
      placeholder = "Select",
      optionValue = [],
      defaultValue = [],
      disabled = false,
      validator,
      required,
      errors,
      onSelect,
      onRemove,
      showCheckbox,
      displayValue,
      groupBy,
      customErrors,
      customErrorsMsg,
      labelSubText,
      errorBorder,
      closeOnSelect = false,
      showSelectAll = false,
    },
    ref
  ) => {
    const selectRef = useRef(null);
    const stateRef = useRef({
      selectedValues: [],
    });
    const mappedOptions = useMemo(
      () =>
        optionValue?.map((opt) => ({
          value: opt.key || opt.id || "",
          label: opt[displayValue] || opt.label || opt.name || opt.key,
          raw: opt,
        })),
      [optionValue, displayValue]
    );

    const initialSelected = useMemo(
      () => mappedOptions.filter((opt) => defaultValue?.some((d) => String(typeof d === "object" ? d.key : d) === String(opt.value))),
      [mappedOptions, defaultValue]
    );

    const [selectedValue, setSelectedValue] = useState(initialSelected);

    useEffect(() => {
      if (!defaultValue?.length) return;
    
      const matched = mappedOptions.filter((opt) =>
        defaultValue?.some(
          (d) => String(typeof d === "object" ? d.key : d) === String(opt.value)
        )
      );
    
      if (matched.length > 0) {
        setSelectedValue(matched);
        stateRef.current.selectedValues = matched.map((v) => v.raw);
      }
    }, [defaultValue, mappedOptions]);

    const isAllSelected = showSelectAll && selectedValue.length === mappedOptions.length;

    const displayOptions = useMemo(() => (showSelectAll ? [ALL_OPTION, ...mappedOptions] : mappedOptions), [showSelectAll, mappedOptions]);

    const selectValue = isAllSelected ? [ALL_OPTION] : selectedValue;

    const api = useMemo(
      () => ({
        resetSelectedValues: (mode = "empty") => {
          if (mode === "all" && showSelectAll) {
            setSelectedValue(mappedOptions);
            stateRef.current.selectedValues = mappedOptions.map((v) => v.raw);
            onSelect?.(
              mappedOptions.map((v) => v.raw),
              undefined
            );
          } else {
            setSelectedValue([]);
            stateRef.current.selectedValues = [];
            onRemove?.([]);
          }
        },
        state: stateRef.current,
        selectRef: selectRef?.current,
      }),
      [onRemove, onSelect, mappedOptions, showSelectAll]
    );

    useImperativeHandle(ref, () => api, [api]);

    useEffect(() => {
      if (validator) {
        validator(api);
      }
    }, [api, validator]);

    const handleChange = (newValue, actionMeta) => {
      if (showSelectAll) {
        const clickedValue = actionMeta.option?.value;
        const isAllOptionClicked = clickedValue === ALL_OPTION.value;

        const allRemoved = (actionMeta.action === "deselect-option" || actionMeta.action === "remove-value") && isAllOptionClicked;

        if (isAllOptionClicked && !allRemoved) {
          const next = isAllSelected ? [] : mappedOptions;
          setSelectedValue(next);
          stateRef.current.selectedValues = next.map((v) => v.raw);
          isAllSelected
            ? onRemove?.([], undefined)
            : onSelect(
                next.map((v) => v.raw),
                undefined
              );
          return;
        }

        if (allRemoved) {
          setSelectedValue([]);
          stateRef.current.selectedValues = [];
          onRemove?.([], undefined);
          return;
        }

        if (isAllSelected && actionMeta.action === "select-option" && !isAllOptionClicked) {
          const filtered = mappedOptions.filter((opt) => opt.value !== clickedValue);
          const values = filtered.map((v) => v.raw);
          setSelectedValue(filtered);
          stateRef.current.selectedValues = values;
          onRemove?.(values, actionMeta.option?.raw);
          return;
        }

        const filtered = (newValue || []).filter((v) => v.value !== ALL_OPTION.value);
        const values = filtered.map((v) => v.raw);
        setSelectedValue(filtered);
        stateRef.current.selectedValues = values;

        if (actionMeta.action === "select-option") {
          onSelect(values, actionMeta.option?.raw);
        } else if (["deselect-option", "remove-value", "pop-value", "clear"].includes(actionMeta.action)) {
          onRemove?.(values, actionMeta.option?.raw);
        }
        return;
      }
      const values = newValue.map((v) => v.raw);
      setSelectedValue(newValue);
      stateRef.current.selectedValues = values;

      if (actionMeta.action === "select-option") {
        onSelect(values, actionMeta.option?.raw);
      } else if (["remove-value", "pop-value", "clear"].includes(actionMeta.action)) {
        onRemove?.(values, actionMeta.option?.raw);
      }
    };

    return (
      <div className={classes?.column || ""}>
        {label && (
          <label htmlFor={id} className={classes?.labelClassName || ""}>
            {label} {required && <span className={styles.astrick}>&#42;</span>}
            {labelSubText && <div className={styles.bir_subline}>{labelSubText}</div>}
          </label>
        )}

        <div className={`${classes?.inputColumn || ""} ${errorBorder ? styles.error_border_color : ""}`}>
          <Select
            inputId={id}
            name={name}
            ref={selectRef}
            isDisabled={disabled}
            isMulti
            isAllSelected={isAllSelected}
            options={displayOptions}
            value={selectValue}
            onChange={handleChange}
            placeholder={placeholder}
            closeMenuOnSelect={closeOnSelect}
            hideSelectedOptions={false}
            components={{
              Option: (props) => <CheckboxOption {...props} showCheckbox={showCheckbox} />,
              IndicatorSeparator: () => null,
              ...(showSelectAll && { ValueContainer: AllValueContainer }),
            }}
            styles={{
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected ? "transparent" : state.isFocused ? "#f5f5f5" : "transparent",
                color: "inherit",
              }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
            menuPortalTarget={document.body}
          />
          {customErrors && <span className={`${styles.error_message}`}>{customErrors ? customErrorsMsg : ""}</span>}
        </div>

        {errors && (
          <span className={`col-6 ${id === "exudateType" ? "offset-5" : "offset-6"} px-1 ${styles.error_message}`}>
            {errors[name]?.message || ""}
          </span>
        )}
        {errorBorder && <span className={`col-6 offset-5 p-0 pt-1 ${styles.error_message}`}>Product Manager is required</span>}
      </div>
    );
  }
);
